import { useQuery } from "@tanstack/react-query";
import { leagueApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type { MyLeagueRegistrationPageResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The caller's league registrations across EVERY facility (GET
 * /users/me/league-registrations → MyLeagueRegistrationPageResponse). AUTH-gated and
 * SERVER-SCOPED to the token user — no uid is threaded through; the route resolves
 * the caller from the token. The call fires ONLY when there is a logged-in user
 * (mirrors useMyRegistrations), so an anonymous viewer never triggers it.
 *
 * Each row names the division/season/league/facility (never a bare UUID) and
 * carries the player's standing in that division when one exists. The list is
 * SERVER-ORDERED; the consuming page renders it verbatim and never re-sorts or
 * re-derives a standing. This is the league analogue of useMyRegistrations (the
 * cross-tournament "My entries" read), backing the player's "My leagues" history.
 */
export function useMyLeagueRegistrations(page = 1) {
  const { user } = useAuth();
  return useQuery<MyLeagueRegistrationPageResponse>({
    queryKey: [...queryKeys.me.leagueRegistrations(), user?.uid, { page }],
    queryFn: () => leagueApi.listMyLeagueRegistrations({ limit: 20, offset: (page - 1) * 20 }),
    enabled: Boolean(user),
  });
}
